import { router, usePage } from "@inertiajs/react";
import { Badge, Box, Button, Callout, Checkbox, Dialog, Flex, Heading, Text } from "@radix-ui/themes";
import { AlertCircle, Info, X } from "lucide-react";
import { useState } from "react";
import CarBrowser from "@/shared/cars/CarBrowser";
import MediaBrowser from "@/shared/media/MediaBrowser";
import { SharedProps } from "@/types";
import { EventsShowSelfReports } from "@/types/events_show";

interface Props {
  eventId: number;
  selfReports: EventsShowSelfReports;
}

/**
 * "Who's coming" before the event, "Who was there" from its day on. People
 * say so themselves — nobody approves it — which the note under the heading
 * says out loud.
 */
export default function SelfReportsSection({ eventId, selfReports }: Props) {
  const { day_reached, drivers, media } = selfReports;
  const { props } = usePage<SharedProps & { errors?: { self_report?: string } }>();
  const error = props.errors?.self_report;

  // From the day on, people who confirmed lead and the unconfirmed "going"
  // follow, muted. Before the day, everyone is just going.
  const confirmed = {
    drivers: day_reached ? drivers.filter((d) => d.attended) : drivers,
    media: day_reached ? media.filter((m) => m.attended) : media,
  };
  const unconfirmed = {
    drivers: day_reached ? drivers.filter((d) => !d.attended) : [],
    media: day_reached ? media.filter((m) => !m.attended) : [],
  };
  const nobody = drivers.length === 0 && media.length === 0;

  return (
    <Box mt="8">
      <Heading size="7" mb="1">
        {day_reached ? "Who was there" : "Who's coming"}
      </Heading>
      <Flex align="center" gap="1" mb="4">
        <Info size={14} color="var(--gray-9)" />
        <Text size="2" color="gray">
          Self-reported. These people said they'd be here.
        </Text>
      </Flex>

      {error && (
        <Callout.Root color="red" mb="4">
          <Callout.Icon>
            <AlertCircle size={16} />
          </Callout.Icon>
          <Callout.Text>{error}</Callout.Text>
        </Callout.Root>
      )}

      <Box mb="5">
        <ReportControls eventId={eventId} selfReports={selfReports} signedIn={!!props.currentUser} />
      </Box>

      {nobody ? (
        <Text color="gray">{day_reached ? "Nobody has said they were here yet." : "Nobody has said they're going yet."}</Text>
      ) : (
        <Flex direction="column" gap="6">
          {drivers.length > 0 && (
            <Box>
              <Heading size="5" mb="3">Drivers</Heading>
              <CarBrowser
                reportReasons={selfReports.report_reasons}
                groups={[
                  { key: "confirmed", items: confirmed.drivers },
                  { key: "unconfirmed", title: "Said they were going", muted: true, items: unconfirmed.drivers },
                ]}
              />
            </Box>
          )}
          {media.length > 0 && (
            <Box>
              <Heading size="5" mb="3">Media</Heading>
              <MediaBrowser
                groups={[
                  { key: "confirmed", items: confirmed.media },
                  { key: "unconfirmed", title: "Said they were going", muted: true, items: unconfirmed.media },
                ]}
              />
            </Box>
          )}
        </Flex>
      )}
    </Box>
  );
}

function ReportControls({ eventId, selfReports, signedIn }: Props & { signedIn: boolean }) {
  const { day_reached, allowed, mine, options } = selfReports;
  const [picking, setPicking] = useState(false);
  const [chosen, setChosen] = useState<number[]>([]);
  const base = `/events/${eventId}/self_reports`;
  const action = day_reached ? "I was there" : "I'm going";

  if (!signedIn) {
    return (
      <Text>
        {day_reached ? "Were you there?" : "Going?"}{" "}
        <a href={`/users/sign_in?return_to=/events/${eventId}`} className="text-accent9">
          Sign in to say so
        </a>
        .
      </Text>
    );
  }

  const choices = [
    ...(options?.cars ?? []).map((car) => ({
      participantId: car.participant_id,
      label: car.label,
      note: car.review_status === "approved" ? null : "Waiting on review — only you will see it until it's approved.",
    })),
    ...(options?.media_group
      ? [{ participantId: options.media_group.participant_id, label: `${options.media_group.name} (media)`, note: null }]
      : []),
  ];

  const report = (participantIds: number[]) =>
    router.post(base, { participant_ids: participantIds }, { preserveScroll: true, onSuccess: () => setPicking(false) });

  return (
    <Flex direction="column" gap="3">
      {mine.length > 0 && (
        <Flex direction="column" gap="2">
          {mine.map((report) => (
            <Flex key={report.id} align="center" gap="2" wrap="wrap">
              <Badge color={report.attended ? "green" : "blue"} size="2">
                {report.attended ? "You were there" : "You're going"}
              </Badge>
              <Text size="2">{report.label}</Text>
              {day_reached && !report.attended && (
                <Button
                  size="1"
                  variant="soft"
                  onClick={() => router.patch(`${base}/${report.id}/confirm`, {}, { preserveScroll: true })}
                >
                  I was there
                </Button>
              )}
              <Button
                size="1"
                variant="ghost"
                color="gray"
                aria-label="Remove"
                onClick={() => router.delete(`${base}/${report.id}`, { preserveScroll: true })}
              >
                <X size={14} />
              </Button>
            </Flex>
          ))}
        </Flex>
      )}

      {choices.length === 1 && (
        <Box>
          <Button size="3" onClick={() => report([choices[0].participantId])}>
            {action}
            {mine.length > 0 && ` — ${choices[0].label}`}
          </Button>
        </Box>
      )}

      {choices.length > 1 && (
        <Dialog.Root open={picking} onOpenChange={(open) => { setPicking(open); setChosen([]); }}>
          <Dialog.Trigger>
            <Box>
              <Button size="3">{action}</Button>
            </Box>
          </Dialog.Trigger>
          <Dialog.Content maxWidth="440px">
            <Dialog.Title>{action}</Dialog.Title>
            <Dialog.Description size="2" mb="4" color="gray">
              {day_reached ? "Which did you bring?" : "Which are you bringing?"} Pick as many as apply.
            </Dialog.Description>
            <Flex direction="column" gap="3">
              {choices.map((choice) => (
                <Text as="label" size="2" key={choice.participantId}>
                  <Flex gap="2" align="start">
                    <Checkbox
                      checked={chosen.includes(choice.participantId)}
                      onCheckedChange={(checked) =>
                        setChosen(
                          checked
                            ? [...chosen, choice.participantId]
                            : chosen.filter((id) => id !== choice.participantId),
                        )
                      }
                    />
                    <Flex direction="column">
                      <Text>{choice.label}</Text>
                      {choice.note && <Text size="1" color="amber">{choice.note}</Text>}
                    </Flex>
                  </Flex>
                </Text>
              ))}
            </Flex>
            <Flex gap="3" mt="5" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">Cancel</Button>
              </Dialog.Close>
              <Button disabled={chosen.length === 0} onClick={() => report(chosen)}>
                {action}
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>
      )}

      {choices.length === 0 && mine.length === 0 && (
        <Flex gap="2" wrap="wrap" align="center">
          <Text size="2" color="gray">To say {day_reached ? "you were there" : "you're going"}, first:</Text>
          {allowed.driver && (
            <Button asChild variant="soft" size="2">
              <a href="/driver_cars/new">Add a car to your garage</a>
            </Button>
          )}
          {allowed.media && (
            <Button asChild variant="soft" size="2">
              <a href={`/media_group_participant/new?event_id=${eventId}`}>Set up your media profile</a>
            </Button>
          )}
        </Flex>
      )}
    </Flex>
  );
}
